import { Injectable } from '@angular/core';
import JSZip from 'jszip';

@Injectable({ providedIn: 'root' })
export class WordExportService {
  private readonly FONT = 'Source Sans Regular';
  private readonly FONT_STACK = `'Source Sans Regular', 'Source Sans Pro', 'Segoe UI', sans-serif`;
  private readonly SZ = '20'; // half-points → 10pt

  /**
   * Pulisce l'HTML per l'esportazione Word:
   * - normalizza celle vuote
   * - corregge bordi tabelle (compatibilità Jodit)
   * - se forceDefault=false (default): applica font/size solo sugli elementi che NON
   *   hanno già un valore esplicito, rispettando la formattazione del BE
   * - se forceDefault=true: sovrascrive font e dimensione su tutti gli elementi
   */
  cleanHtmlForWord(html: string, forceDefault = false): string {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Celle vuote → &nbsp; per evitare il collasso
    tempDiv.querySelectorAll('td:empty, th:empty').forEach((cell) => {
      cell.innerHTML = '&nbsp;';
    });

    // Dimensioni minime e fix bordi celle
    tempDiv.querySelectorAll<HTMLElement>('td, th').forEach((cell) => {
      cell.style.minHeight = '20px';
      cell.style.minWidth = '50px';
      cell.style.padding = '5px';

      const computedBorder = window.getComputedStyle(cell).borderStyle;
      if (
        computedBorder === 'hidden' ||
        computedBorder === 'none' ||
        cell.style.borderStyle === 'hidden' ||
        cell.style.borderStyle === 'none'
      ) {
        cell.style.border = 'none';
        cell.style.borderStyle = 'none';
      } else {
        // Jodit salva solo border-color: aggiungi width e style se mancano
        const hasBorderColor = !!cell.style.borderColor;
        if (hasBorderColor) {
          if (!cell.style.borderWidth) cell.style.borderWidth = '1px';
          if (!cell.style.borderStyle) cell.style.borderStyle = 'solid';
        }
      }
    });

    // Fix bordi e border-collapse sulle tabelle
    tempDiv.querySelectorAll<HTMLElement>('table').forEach((table) => {
      const bs = table.style.borderStyle;
      if (bs === 'hidden' || bs === 'none') {
        table.style.border = 'none';
        table.style.borderStyle = 'none';
      } else if (!table.style.borderCollapse) {
        table.style.borderCollapse = 'collapse';
      }
    });

    // Converti font-size da px a pt PRIMA di applicare i default
    // (html-docx-js interpreta i valori numerici come pt, non px)
    tempDiv.querySelectorAll<HTMLElement>('[style]').forEach((el) => {
      if (el.style.fontSize) {
        const match = el.style.fontSize.match(/^([\d.]+)px$/);
        if (match) {
          el.style.fontSize = `${parseFloat(match[1])}pt`;
        }
      }
    });

    // Applica font/size di default
    if (forceDefault) {
      // Override totale: sovrascrive qualsiasi stile preesistente
      tempDiv.querySelectorAll<HTMLElement>('*').forEach((el) => {
        el.style.fontFamily = this.FONT_STACK;
        el.style.fontSize = '10pt';
      });
    } else {
      // Priorità al BE: imposta il default solo se l'elemento non ha già un valore esplicito
      tempDiv.querySelectorAll<HTMLElement>('*').forEach((el) => {
        if (!el.style.fontFamily) el.style.fontFamily = this.FONT_STACK;
        if (!el.style.fontSize) el.style.fontSize = '10pt';
      });
    }

    // Propaga la formattazione bold ai nodi di testo tra due <strong>/<b> adiacenti
    // (Jodit non può wrappare i placeholder contenteditable="false" in <strong>)
    tempDiv.querySelectorAll<Element>('p, li, td, th').forEach((block) => {
      const children = Array.from(block.childNodes);
      children.forEach((child, index) => {
        if (child.nodeType !== Node.TEXT_NODE || !(child.textContent?.trim())) return;
        let prevEl: HTMLElement | null = null;
        for (let i = index - 1; i >= 0; i--) {
          if (children[i].nodeType === Node.ELEMENT_NODE) { prevEl = children[i] as HTMLElement; break; }
        }
        let nextEl: HTMLElement | null = null;
        for (let i = index + 1; i < children.length; i++) {
          if (children[i].nodeType === Node.ELEMENT_NODE) { nextEl = children[i] as HTMLElement; break; }
        }
        const prevIsBold = prevEl && (prevEl.tagName === 'STRONG' || prevEl.tagName === 'B');
        const nextIsBold = nextEl && (nextEl.tagName === 'STRONG' || nextEl.tagName === 'B');
        if (prevIsBold && nextIsBold) {
          const strong = document.createElement('strong');
          strong.textContent = child.textContent || '';
          child.parentNode?.replaceChild(strong, child);
        }
      });
    });

    return tempDiv.innerHTML;
  }

  /**
   * Avvolge l'HTML con la dichiarazione DOCTYPE, encoding UTF-8 e CSS font base.
   * Se forceDefault=false (default) la regola CSS NON usa !important: gli stili inline
   * del BE hanno precedenza naturale. Se forceDefault=true usa !important per
   * sovrascrivere tutto.
   */
  wrapHtmlForWord(html: string, forceDefault = false): string {
    const imp = forceDefault ? ' !important' : '';
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    body, p, div, span, li, td, th, h1, h2, h3, h4, h5, h6 {
      font-family: ${this.FONT_STACK}${imp};
      font-size: 10pt;
    }
  </style>
</head>
<body>${html}</body>
</html>`;
  }

  /**
   * Genera e scarica un documento Word a partire dall'HTML fornito.
   * Applica pulizia, wrapping, conversione via html-docx-js e iniezione footer.
   * @param forceDefault se true sovrascrive font/size su tutti gli elementi;
   *                     se false (default) rispetta la formattazione esistente del BE.
   */
  generateAndDownload(html: string, filename: string, forceDefault = false): void {
    try {
      const cleaned = this.cleanHtmlForWord(html, forceDefault);
      const wrapped = this.wrapHtmlForWord(cleaned, forceDefault);
      const rawBlob: Blob = (window as any).htmlDocx.asBlob(wrapped);
      this.addWordFooter(rawBlob)
        .then((blob) => this.triggerDownload(blob, filename))
        .catch((err) => console.error('Error generating Word:', err));
    } catch (err) {
      console.error('Error generating Word:', err);
    }
  }

  /**
   * Inietta nel DOCX un footer con numero pagina (destra) e testo "Internal" (centro)
   * tramite JSZip, senza dipendere da word/styles.xml (non generato da html-docx-js).
   */
  async addWordFooter(docxBlob: Blob): Promise<Blob> {
    const fontAttrs = `w:ascii="${this.FONT}" w:hAnsi="${this.FONT}" w:eastAsia="${this.FONT}" w:cs="${this.FONT}"`;
    const FOOTER_ID = 'rId_ftr1';

    try {
      const zip = await JSZip.loadAsync(docxBlob);

      const rpr = `<w:rPr><w:rFonts ${fontAttrs}/><w:sz w:val="${this.SZ}"/><w:szCs w:val="${this.SZ}"/></w:rPr>`;
      const fld = (instr: string) =>
        `<w:r>${rpr}<w:fldChar w:fldCharType="begin"/></w:r>` +
        `<w:r><w:instrText xml:space="preserve"> ${instr} </w:instrText></w:r>` +
        `<w:r><w:fldChar w:fldCharType="separate"/></w:r>` +
        `<w:r><w:t>1</w:t></w:r>` +
        `<w:r><w:fldChar w:fldCharType="end"/></w:r>`;

      const footerXml =
        `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
        `<w:ftr xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"` +
        ` xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">` +
        `<w:p><w:pPr><w:jc w:val="right"/><w:spacing w:before="160"/></w:pPr>${fld('PAGE')}</w:p>` +
        `<w:p><w:pPr><w:jc w:val="center"/></w:pPr><w:r>${rpr}<w:t>Internal</w:t></w:r></w:p>` +
        `</w:ftr>`;

      zip.file('word/footer1.xml', footerXml);

      // word/styles.xml — patch font se presente (fallback per DOCX con stili propri)
      const stylesFile = zip.file('word/styles.xml');
      if (stylesFile) {
        let stylesXml: string = await stylesFile.async('text');
        stylesXml = stylesXml.replace(/<w:rFonts[^>]*\/>/g, `<w:rFonts ${fontAttrs}/>`);
        const docDefaultsRpr =
          `<w:rPr><w:rFonts ${fontAttrs}/><w:sz w:val="${this.SZ}"/><w:szCs w:val="${this.SZ}"/></w:rPr>`;
        if (stylesXml.includes('<w:docDefaults>')) {
          if (stylesXml.includes('<w:rPrDefault>')) {
            stylesXml = stylesXml.replace(
              /<w:rPrDefault>.*?<\/w:rPrDefault>/s,
              `<w:rPrDefault>${docDefaultsRpr}</w:rPrDefault>`
            );
          } else {
            stylesXml = stylesXml.replace('<w:docDefaults>', `<w:docDefaults><w:rPrDefault>${docDefaultsRpr}</w:rPrDefault>`);
          }
        } else {
          stylesXml = stylesXml.replace('<w:style ', `<w:docDefaults><w:rPrDefault>${docDefaultsRpr}</w:rPrDefault></w:docDefaults><w:style `);
        }
        zip.file('word/styles.xml', stylesXml);
      }

      // [Content_Types].xml
      const ctXml: string = await zip.file('[Content_Types].xml')!.async('text');
      if (!ctXml.includes('footer1.xml')) {
        zip.file(
          '[Content_Types].xml',
          ctXml.replace(
            '</Types>',
            '<Override PartName="/word/footer1.xml"' +
              ' ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.footer+xml"/>' +
              '</Types>'
          )
        );
      }

      // word/_rels/document.xml.rels
      const relsFile = zip.file('word/_rels/document.xml.rels');
      if (relsFile) {
        const relsXml: string = await relsFile.async('text');
        if (!relsXml.includes(FOOTER_ID)) {
          zip.file(
            'word/_rels/document.xml.rels',
            relsXml.replace(
              '</Relationships>',
              `<Relationship Id="${FOOTER_ID}"` +
                ` Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/footer"` +
                ` Target="footer1.xml"/></Relationships>`
            )
          );
        }
      }

      // word/document.xml — footerReference + rFonts
      const docFile = zip.file('word/document.xml');
      if (docFile) {
        let docXml: string = await docFile.async('text');
        docXml = docXml.replace(/<w:rFonts[^>]*\/>/g, `<w:rFonts ${fontAttrs}/>`);
        if (!docXml.includes(FOOTER_ID)) {
          const footerRef = `<w:footerReference w:type="default" r:id="${FOOTER_ID}"/>`;
          docXml = /<w:sectPr[\s>]/.test(docXml)
            ? docXml.replace(/<\/w:sectPr>/, `${footerRef}</w:sectPr>`)
            : docXml.replace('</w:body>', `<w:sectPr>${footerRef}</w:sectPr></w:body>`);
          zip.file('word/document.xml', docXml);
        }
      }

      return zip.generateAsync({
        type: 'blob',
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      }) as Promise<Blob>;
    } catch (err) {
      console.error('Footer injection failed:', err);
      return docxBlob;
    }
  }

  private triggerDownload(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }
}
